import React, { useEffect } from "react";
import { Accordion, CmsContent, GravityForm, Image, gravityFormExtractor } from "components";
import { useAllGravityForms } from "hooks";
import AOS from "aos";
import "aos/dist/aos.css";
import ReactPlayer from "react-player";

/** Switch statement which will return the correct content type. To be reused in other components */
interface ColumnSwitchProps {
  data: {
    accordion: null;
    content: string | null;
    contentType: string;
    formId: string | null;
    iframe: string | null;
    image: { localFile } | null;
    structuredData: boolean | null;
    video: string | null;
  };
}

/**
 * Sorts through content types and returns the correct format inside a column
 *
 * @param data - Object. All of the different data, supports accordion, text content, form, iframe, image and video
 * @returns Returns content type in column container
 *
 */
export const ColumnSwitch: React.FC<ColumnSwitchProps> = ({ data }): JSX.Element => {
  const gravityFormsData = useAllGravityForms();
  useEffect(() => {
    AOS.init();
  }, []);
  switch (data.contentType) {
    case "image":
      return (
        <div className="column column--image" data-aos="fade-up">
          <Image src={data.image} alt="" />
        </div>
      );

    case "content":
      return (
        <div className="column column--text" data-aos="fade-up">
          <CmsContent content={data.content} />
        </div>
      );

    case "form":
      const formData = gravityFormExtractor(parseInt(data.formId), gravityFormsData);
      return (
        <div className="column column--form">
          <GravityForm
            formData={formData}
            dataLayerEvent={"form_submit_success"}
            displayTitle={false}
            displayDescription={false}
          />
        </div>
      );

    case "video":
      return (
        <div className="column column--video" data-aos="fade-up">
          <div className="react-player-wrapper">
            <ReactPlayer url={data.video} className="react-player" width="100%" height="100%" />
          </div>
        </div>
      );

    case "iframe":
      return (
        <div className="column column--iframe" data-aos="fade-up">
          <iframe src={data.iframe} frameBorder="0" allowFullScreen loading="lazy" />
        </div>
      );

    case "accordion":
      return (
        <div className="column column--accordion" data-aos="fade-up">
          <Accordion rows={data.accordion} intro={data.accordionIntro} schema={data.structuredData} />
        </div>
      );

    default:
      return <p>The content type: {data.contentType} doesnt exist</p>;
  }
};
